import React from 'react';
import CompactMonitoringChart from './CompactMonitoringChart';
import MultiPaneChart from './MultiPaneChart';
import SeasonalTendency from './SeasonalTendency';
import PairTradingSection from './PairTradingSection';
import LockOverlay from '../Auth/LockOverlay';
import { useChartData } from '../../hooks/useChartData';

const TradingChart = ({
  marketData,
  symbol = 'VCB.VN',
  showVolume = true,
  showSuperTrend = true,
  indicatorConfig = 'advanced',
  selectedTimeframe,
  setSelectedTimeframe,
  isAuthed = true,
  onRequestLogin = () => {},
  regimeSlot = null, // Regime Intelligence — right rail on xl, stacked below the chart otherwise
}) => {
  // Custom hook for data management
  const {
    allCandleData,
  } = useChartData(marketData);

  return (
    <div className="relative w-full space-y-4">
      {/* 1. Top strip: Trend / RSI / OBV / DCA (mini-charts open; win-rate
          stats gated inside for logged-out visitors). */}
      <div id="tour-strip">
        <CompactMonitoringChart
          candleData={allCandleData.length > 0 ? allCandleData : marketData?.candles}
          height={150}
          symbol={symbol}
          isAuthed={isAuthed}
          onRequestLogin={onRequestLogin}
        />
      </div>

      {/* 2. Dashboard row — xl: seasonal rail | main chart | regime rail.
          Below xl it stacks in reading order: seasonal, then regime, then
          the chart (strip → seasonal → regime → chart → pair). */}
      <div className="grid grid-cols-1 xl:grid-cols-[200px_minmax(0,1fr)_250px] gap-4 items-stretch">
        <div id="tour-seasonal" className="order-1 xl:order-none xl:col-start-1 xl:row-start-1 min-w-0">
          {isAuthed ? (
            <SeasonalTendency symbol={symbol} />
          ) : (
            <LockOverlay
              title="Seasonal Tendency"
              subtitle="Day/month/quarter edges"
              onRequestLogin={() => onRequestLogin('Seasonal tendency — historical calendar edges')}
              minHeight={130}
            >
              <SeasonalTendency symbol={symbol} />
            </LockOverlay>
          )}
        </div>
        <div id="tour-chart" className="order-3 xl:order-none xl:col-start-2 xl:row-start-1 min-w-0">
          <MultiPaneChart
            marketData={marketData}
            symbol={symbol}
            height={640} // fixed — volume/MACD/RSI overlay inside, price stays big
            showVolume={showVolume}
            showRSI={false}
            showMACD={false}
            showSuperTrend={showSuperTrend}
            indicatorConfig={indicatorConfig}
            showRegimeBackground={false}
            timeframe={selectedTimeframe}
            onTimeframeChange={setSelectedTimeframe}
            isAuthed={isAuthed}
            onRequestLogin={onRequestLogin}
          />
        </div>
        <div id="tour-regime" className="order-2 xl:order-none xl:col-start-3 xl:row-start-1 min-w-0">
          {isAuthed ? (
            regimeSlot
          ) : (
            <LockOverlay
              title="Regime Intelligence"
              subtitle="Market regime & quality model"
              onRequestLogin={() => onRequestLogin('Regime Intelligence — the market-regime & quality model')}
              minHeight={130}
            >
              {regimeSlot}
            </LockOverlay>
          )}
        </div>
      </div>

      {/* 3. Pair trading at the bottom */}
      <div id="tour-pair">
      {isAuthed ? (
        <PairTradingSection symbol={symbol} onPairData={() => {}} />
      ) : (
        <LockOverlay
          title="Pair Trading"
          subtitle="Cointegration & z-score backtests"
          onRequestLogin={() => onRequestLogin('Pair Trading — cointegration checks & backtested spread signals')}
          minHeight={96}
        >
          <PairTradingSection symbol={symbol} onPairData={() => {}} />
        </LockOverlay>
      )}
      </div>
    </div>
  );
};

export default TradingChart;
